import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { DiaryWorkPhaseDto } from '../../types/DiaryWorkPhaseDto';
import { ObjectDiaryDetailDto } from '../../types/ObjectDiaryDetailDto';
import logo from '../../assets/114.jpg';
import { formatFinnishDate } from '../../utilities/PdfUtils';

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontSize: 10,
        fontFamily: 'Helvetica',
    },
    header: {
        marginBottom: 20,
    },
    logoRow: {
        flexDirection: 'row',
        marginBottom: 15,
    },
    logoCell: {
        width: '60%',
    },
    logo: {
        width: 150,
        height: 'auto',
    },
    emptyCell: {
        width: '40%',
    },
    spacer: {
        marginBottom: 10,
    },
    headerRowTwoCol: {
        flexDirection: 'row',
        marginBottom: 5,
        fontSize: 10,
    },
    headerCellLeft: {
        width: '60%',
    },
    headerCellRight: {
        width: '40%',
    },
    headerCellBold: {
        fontWeight: 'bold',
    },
    headerCellLabel: {
        fontWeight: 'bold',
    },
    title: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'right',
    },
    sectionTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        backgroundColor: '#e9ecef',
        padding: 8,
        marginTop: 15,
        marginBottom: 5,
        color: '#1c7ed6',
    },
    table: {
        marginTop: 5,
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: '#dee2e6',
        borderBottomWidth: 2,
        borderBottomColor: '#495057',
        paddingVertical: 8,
        paddingHorizontal: 4,
        fontWeight: 'bold',
        fontSize: 8,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        fontSize: 8,
    },
    tableRowAlt: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingVertical: 6,
        paddingHorizontal: 4,
        backgroundColor: '#f8f9fa',
        fontSize: 8,
    },
    // Work phases table columns
    wpCol1: { width: '40%' },
    wpCol2: { width: '20%', textAlign: 'center' },
    wpCol3: { width: '20%', textAlign: 'center' },
    wpCol4: { width: '20%', textAlign: 'center' },
    // Info fields
    fieldRow: {
        flexDirection: 'row',
        marginBottom: 6,
        fontSize: 10,
    },
    fieldLabel: {
        width: '35%',
        fontWeight: 'bold',
    },
    fieldValue: {
        width: '65%',
    },
    // Diary entry card
    entryCard: {
        borderWidth: 1,
        borderColor: '#dee2e6',
        borderRadius: 4,
        padding: 10,
        marginBottom: 10,
    },
    entryHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: '#dee2e6',
        paddingBottom: 6,
    },
    entryDate: {
        fontSize: 11,
        fontWeight: 'bold',
    },
    entryFieldRow: {
        flexDirection: 'row',
        marginBottom: 4,
        fontSize: 9,
    },
    entryFieldLabel: {
        width: '30%',
        fontWeight: 'bold',
        color: '#495057',
    },
    entryFieldValue: {
        width: '70%',
    },
    entryTextBlock: {
        marginTop: 4,
        marginBottom: 4,
    },
    entryTextLabel: {
        fontSize: 9,
        fontWeight: 'bold',
        color: '#495057',
        marginBottom: 2,
    },
    entryTextValue: {
        fontSize: 9,
        lineHeight: 1.4,
    },
    footer: {
        position: 'absolute',
        bottom: 30,
        left: 30,
        right: 30,
        fontSize: 8,
        color: '#666',
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    noEntries: {
        textAlign: 'center',
        color: '#999',
        padding: 20,
        fontSize: 10,
    },
});

interface DailyDiaryPDFProps {
    workPhases: DiaryWorkPhaseDto[];
    diaryEntries: ObjectDiaryDetailDto[];
    contractorWorkNumber?: string;
    measurementDate?: Date | null;
    measurementPersons?: string;
    locationMeasurementDate?: Date | null;
    locationMeasurementPersons?: string;
    form?: any;
    contractName?: string;
    getTranslation?: (key: string) => string | null;
}

export const DailyDiaryPDF = ({
    workPhases,
    diaryEntries,
    contractorWorkNumber,
    measurementDate,
    measurementPersons,
    locationMeasurementDate,
    locationMeasurementPersons,
    form,
    contractName,
    getTranslation,
}: DailyDiaryPDFProps) => {
    const t = (key: string, fallback: string) =>
        getTranslation ? (getTranslation(key) || fallback) : fallback;

    const jobName = contractName
        || form?.values?.jobName
        || form?.values?.contractName
        || '';
    const location = form?.values?.location || form?.values?.name || '';

    return (
        <Document>
            <Page size="A4" orientation="portrait" style={styles.page}>
                {/* Header */}
                <View style={styles.header}>
                    <View style={styles.logoRow}>
                        <View style={styles.logoCell}>
                            <Image src={logo} style={styles.logo} />
                        </View>
                        <View style={styles.emptyCell} />
                    </View>

                    <View style={styles.spacer} />

                    <View style={styles.headerRowTwoCol}>
                        <Text style={[styles.headerCellLeft, styles.headerCellBold]}>
                            {jobName}
                        </Text>
                        <Text style={[styles.headerCellRight, styles.title, { textAlign: 'left' }]}>
                            {t('TITLE_DIARY', 'DIARY')}
                        </Text>
                    </View>
                    {location ? (
                        <View style={styles.headerRowTwoCol}>
                            <Text style={styles.headerCellLeft}>
                                <Text style={styles.headerCellLabel}>{t('LABEL_LOCATION', 'Location')}: </Text>
                                {location}
                            </Text>
                            <Text style={[styles.headerCellRight, styles.headerCellBold]}>
                                {formatFinnishDate()}
                            </Text>
                        </View>
                    ) : (
                        <View style={styles.headerRowTwoCol}>
                            <Text style={styles.headerCellLeft} />
                            <Text style={[styles.headerCellRight, styles.headerCellBold]}>
                                {formatFinnishDate()}
                            </Text>
                        </View>
                    )}
                </View>

                {/* Work Phases Table */}
                <Text style={styles.sectionTitle}>
                    {t('LABEL_OPERATION_STAGE_NAME', 'Work Phases')}
                </Text>
                <View style={styles.table}>
                    <View style={styles.tableHeader}>
                        <Text style={styles.wpCol1}>{t('LABEL_OPERATION_STAGE_NAME', 'Operation Stage Name')}</Text>
                        <Text style={styles.wpCol2}>{t('LABEL_BEGINNING', 'Beginning')}</Text>
                        <Text style={styles.wpCol3}>{t('LABEL_ENDING', 'Ending')}</Text>
                        <Text style={styles.wpCol4}>{t('LABEL_NO_DATES', 'No dates')}</Text>
                    </View>
                    {workPhases.length === 0 ? (
                        <View style={styles.tableRow}>
                            <Text style={styles.noEntries}>-</Text>
                        </View>
                    ) : (
                        workPhases.map((wp, idx) => (
                            <View key={wp.id ?? idx} style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}>
                                <Text style={styles.wpCol1}>
                                    {wp.description || '-'}
                                    {wp.mandate === 'Yes' ? ' *' : ''}
                                </Text>
                                <Text style={styles.wpCol2}>
                                    {wp.startDate ? formatFinnishDate(wp.startDate) : '-'}
                                </Text>
                                <Text style={styles.wpCol3}>
                                    {wp.endDate ? formatFinnishDate(wp.endDate) : '-'}
                                </Text>
                                <Text style={styles.wpCol4}>
                                    {wp.noDates ? 'X' : ''}
                                </Text>
                            </View>
                        ))
                    )}
                </View>

                {/* Perception Info */}
                <Text style={styles.sectionTitle}>
                    {t('LABEL_ADDITIONAL_INFO', 'Additional Information')}
                </Text>
                <View style={{ marginTop: 8 }}>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>
                            {t('LABEL_CONTRACTORS_JOB_NUMBER', "Contractor's job number")}:
                        </Text>
                        <Text style={styles.fieldValue}>{contractorWorkNumber || '-'}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>
                            {t('LABEL_MEASUREMENT_PVM', 'Measurement date')}:
                        </Text>
                        <Text style={styles.fieldValue}>
                            {measurementDate ? formatFinnishDate(measurementDate) : '-'}
                        </Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>
                            {t('LABEL_PERFORMERS_OF_THE_MEASUREMENT', 'Measurement performers')}:
                        </Text>
                        <Text style={styles.fieldValue}>{measurementPersons || '-'}</Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>
                            {t('LABEL_POSITION_MEASUREMENT_PVM', 'Position measurement date')}:
                        </Text>
                        <Text style={styles.fieldValue}>
                            {locationMeasurementDate ? formatFinnishDate(locationMeasurementDate) : '-'}
                        </Text>
                    </View>
                    <View style={styles.fieldRow}>
                        <Text style={styles.fieldLabel}>
                            {t('LABEL_PERFORMERS_OF_LOCATION_MEASUREMENT', 'Location measurement performers')}:
                        </Text>
                        <Text style={styles.fieldValue}>{locationMeasurementPersons || '-'}</Text>
                    </View>
                </View>

                {/* Diary Entries */}
                <Text style={styles.sectionTitle}>
                    {t('LABEL_DIARY_ENTRIES', 'Diary Entries')}
                </Text>

                {diaryEntries.length === 0 ? (
                    <Text style={styles.noEntries}>
                        {t('LABEL_NO_DIARY_ENTRIES_YET', 'No diary entries yet')}
                    </Text>
                ) : (
                    diaryEntries.map((entry) => (
                        <View key={entry.id} style={styles.entryCard} wrap={false}>
                            <View style={styles.entryHeader}>
                                <Text style={styles.entryDate}>
                                    {entry.diaryDate ? formatFinnishDate(entry.diaryDate) : t('LABEL_NO_DATE', 'No date')}
                                </Text>
                                <Text style={{ fontSize: 8, color: '#666' }}>#{entry.id}</Text>
                            </View>

                            {/* Supervisor / Foreman row */}
                            <View style={styles.entryFieldRow}>
                                <Text style={styles.entryFieldLabel}>
                                    {t('LABEL_SUPERVISOR', 'Supervisor')}:
                                </Text>
                                <Text style={styles.entryFieldValue}>
                                    {entry.supervisorName || entry.supervisor || '-'}
                                </Text>
                            </View>
                            <View style={styles.entryFieldRow}>
                                <Text style={styles.entryFieldLabel}>
                                    {t('LABEL_FOREMAN', 'Foreman')}:
                                </Text>
                                <Text style={styles.entryFieldValue}>
                                    {entry.foremanName || entry.foreman || '-'}
                                </Text>
                            </View>
                            {(entry.viseForemanName || entry.frontman) && (
                                <View style={styles.entryFieldRow}>
                                    <Text style={styles.entryFieldLabel}>
                                        {t('LABEL_VICE_FOREMAN', 'Vice Foreman')}:
                                    </Text>
                                    <Text style={styles.entryFieldValue}>
                                        {entry.viseForemanName || entry.frontman || '-'}
                                    </Text>
                                </View>
                            )}

                            {/* Weather */}
                            {entry.weather && (
                                <View style={styles.entryFieldRow}>
                                    <Text style={styles.entryFieldLabel}>
                                        {t('LABEL_WEATHER', 'Weather')}:
                                    </Text>
                                    <Text style={styles.entryFieldValue}>{entry.weather}</Text>
                                </View>
                            )}

                            {/* Workers */}
                            {entry.workers && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_DIARY_WORKING_GROUP', 'Working group')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.workers}</Text>
                                </View>
                            )}

                            {/* Equipment / Machines */}
                            {entry.machines && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_DIARY_PERSONNEL_EQUIPMENT', 'Personnel and equipment')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.machines}</Text>
                                </View>
                            )}

                            {/* Diesel / MPO consumption */}
                            {(entry.dieselConsumption || entry.mpoConsumption) && (
                                <View style={styles.entryFieldRow}>
                                    <Text style={styles.entryFieldLabel}>
                                        {t('LABEL_DIESEL', 'Diesel')}: {entry.dieselConsumption ?? 0}
                                        {'   '}
                                        {t('LABEL_MPO', 'MPO')}: {entry.mpoConsumption ?? 0}
                                    </Text>
                                </View>
                            )}

                            {/* Tasks / Job remarks */}
                            {entry.tasks && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_JOB_REMARKS', 'Job remarks')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.tasks}</Text>
                                </View>
                            )}

                            {/* Subscriber notices */}
                            {entry.subscriberNotices && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_CLIENT_REMARKS', 'Client remarks')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.subscriberNotices}</Text>
                                </View>
                            )}

                            {/* Notices to subscriber */}
                            {entry.noticesToSubscriber && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_NOTES_TO_CLIENT', 'Notes to client')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.noticesToSubscriber}</Text>
                                </View>
                            )}

                            {/* Description / Others */}
                            {entry.description && (
                                <View style={styles.entryTextBlock}>
                                    <Text style={styles.entryTextLabel}>
                                        {t('LABEL_OTHERS', 'Others')}:
                                    </Text>
                                    <Text style={styles.entryTextValue}>{entry.description}</Text>
                                </View>
                            )}
                        </View>
                    ))
                )}

                {/* Footer */}
                <View style={styles.footer} fixed>
                    <Text>{jobName}</Text>
                    <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
                </View>
            </Page>
        </Document>
    );
};
